'use client';

import { Button, Icon } from '@benzinga/core-ui';
import { faSearch } from '@fortawesome/pro-regular-svg-icons/faSearch';
import { faTimes } from '@fortawesome/pro-regular-svg-icons/faTimes';
import { Ticker } from '@benzinga/session';
import styles from './SearchBar.module.scss';

export interface SearchBarProps {
  autoFocus?: boolean;
  handleClickCancel?: () => void;
  handleSearch?: () => void;
  handleRemove?: (removed: Ticker) => void;
  showSearchIcon?: boolean;
  isFullScreenSearch?: boolean;
  onBlur?: () => void;
  onFocus?: () => void;
  onInput: React.InputEventHandler<HTMLInputElement>;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  placeholder?: string;
  inputRef?: React.MutableRefObject<HTMLInputElement | null>;
  searchQuery?: string;
  selected?: Ticker[] | Ticker | null;
  showSearchButton?: boolean;
}

export const SearchBar: React.FC<SearchBarProps> = ({
  autoFocus,
  handleClickCancel,
  handleRemove,
  handleSearch,
  inputRef,
  isFullScreenSearch,
  onBlur,
  onFocus,
  onInput,
  onKeyDown,
  placeholder,
  searchQuery,
  selected,
  showSearchButton = false,
  showSearchIcon = true,
}) => {
  return (
    <div className={styles.searchInputContainer + ' search-input-container'}>
      <div
        className={`${styles.searchInputWrapper} search-input-wrapper${!showSearchIcon ? ` ${styles.noSearchIcon}` : ''}`}
        style={{ flexDirection: Array.isArray(selected) && selected.length && handleRemove ? 'column' : 'row' }}
      >
        {showSearchIcon && (typeof selected == 'undefined' || (Array.isArray(selected) && selected.length < 1)) && (
          <div className="search-icon" role="presentation">
            <Icon icon={faSearch} />
          </div>
        )}
        {selected && !Array.isArray(selected) && handleRemove ? (
          <SelectedOption key={(selected as Ticker)?.symbol} onRemove={handleRemove} selected={selected as Ticker} />
        ) : Array.isArray(selected) && selected.length && handleRemove ? (
          <div className="w-full" style={{ display: 'flex', flexWrap: 'wrap' }}>
            {(selected as Ticker[]).map(option => (
              <SelectedOption key={option.symbol} onRemove={handleRemove} selected={option} />
            ))}
          </div>
        ) : null}
        <input
          aria-label="Search"
          autoFocus={autoFocus}
          className="search-input"
          id="searchbar-input"
          onBlur={onBlur}
          onFocus={onFocus}
          onInput={onInput}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          ref={inputRef}
          role="searchbox"
          tabIndex={0}
          type="search"
          value={searchQuery}
        />
        {isFullScreenSearch && handleClickCancel && (
          <button className="cancel-button" onClick={handleClickCancel}>
            <Icon icon={faTimes} />
          </button>
        )}
        {showSearchButton && (
          <Button className="search-button" onClick={handleSearch} variant="primary">
            <Icon icon={faSearch} />
          </Button>
        )}
      </div>
    </div>
  );
};

export default SearchBar;

export const SelectedOption = ({ onRemove, selected }: { onRemove: (value: Ticker) => void; selected: Ticker }) => (
  <div className={styles.selectedOptionWrapper}>
    <span>{selected?.symbol}</span>
    <div onClick={() => onRemove(selected)}>
      <Icon icon={faTimes} />
    </div>
  </div>
);
